import { SectionConfig } from "@services/tracpage/types/trac-page-section-config";
import React, { FC, useMemo } from "react";
import { OrderButtons } from "../order-buttons";
import { useEditMode } from "../../../contexts/edit";
import { DEFAULT_SECTIONS_CONFIG } from "../../../constants/default-sections-config";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";

type Props = {
  sectionId: SectionIdentifier;
};

export const SectionOrderButtons: FC<Props> = ({ sectionId }) => {
  const {
    draft,
    editHandlers: { onSectionsReorder },
  } = useEditMode();

  const sectionsConfig = useMemo(
    () => draft?.sectionsConfig ?? DEFAULT_SECTIONS_CONFIG,
    [draft]
  );

  const { minOrder, maxOrder } = useMemo(() => {
    const allOrders = Object.values(sectionsConfig).map((s) => s.order);
    const min = Math.min(...allOrders);
    const max = Math.max(...allOrders);
    return { minOrder: min, maxOrder: max };
  }, [sectionsConfig]);

  const handleReorder = (dir: "up" | "down") => {
    if (!draft) return;
    const sections = Object.values(sectionsConfig);

    const currentOrder = sectionsConfig[sectionId].order;
    if (
      (currentOrder === minOrder && dir === "up") ||
      (currentOrder === maxOrder && dir === "down")
    ) {
      return;
    }

    const newOrder = currentOrder + (dir === "up" ? -1 : 1);

    const sectionWithNewOrder =
      sections.find((s) => s.order === newOrder) ?? null;
    if (!sectionWithNewOrder) return;

    const updatedConfig: SectionConfig = {
      ...sectionsConfig,
      [sectionId]: { ...sectionsConfig[sectionId], order: newOrder },
      [sectionWithNewOrder.id]: {
        ...sectionsConfig[sectionWithNewOrder.id],
        order: sectionWithNewOrder.order + (dir === "up" ? 1 : -1),
      },
    };

    onSectionsReorder(updatedConfig);
  };

  return (
    <OrderButtons
      disabledState={{
        up: sectionsConfig[sectionId].order === minOrder,
        down: sectionsConfig[sectionId].order === maxOrder,
      }}
      onReorder={handleReorder}
    />
  );
};
